import React, { useState } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../MyAppText';
import MyDropDownPicker, { ValueType } from '../../MyDropDownPicker';
import { DropDownPickerProps } from 'react-native-dropdown-picker';
import Tooltip from '../../Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DistributiveOmit<T, K extends keyof any> = T extends any
    ? Omit<T, K>
    : never;

type Props = DistributiveOmit<DropDownPickerProps<ValueType>, 'open' | 'setOpen'>
    & {
        label?: string;
        onChange?: () => void;
        labelFontSize?: number;
        labelStyle?: StyleProp<TextStyle>;
        dropdownLabelStyle?: StyleProp<TextStyle>;
        viewStyle?: StyleProp<TextStyle>;
        required?: boolean;
        semiBoldLabel?: boolean;
        showTooltip?: boolean;
        tooltipText?: string;
        isLoading?: boolean;
        hideLabel?: boolean;
    };

function FilterDropdown({ label, labelFontSize, labelStyle, dropdownLabelStyle, viewStyle, required, showTooltip, tooltipText, semiBoldLabel = false, hideLabel = false, ...props }: Props) {
    const { styles, theme } = useStyles(stylesheet);

    const [isOpen, setIsOpen] = useState(false);

    return (
        <View style={[styles.inputContainer(props.zIndex), viewStyle]}>
            {label && !hideLabel ?
                <View style={{ flexDirection: 'row', gap: 5, zIndex: (props.zIndex || 0) + 1 }}>
                    <MyAppText
                        style={labelStyle ? labelStyle : styles.filterText(labelFontSize ?? 16, semiBoldLabel)}>
                        {label}
                    </MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                    {showTooltip && tooltipText && <Tooltip
                        useSimpleBox
                        style={{
                            color: theme.colors.white['500'],
                            padding: 16,
                            borderRadius: 12,
                            width: 150,
                            justifyContent: 'center',
                            zIndex: 9999,
                            background: theme.colors.neutral['500'],
                        }}
                        tooltipContent={tooltipText}
                        tooltipDirection='right'
                    >
                        <FontAwesomeIcon icon={faCircleInfo as IconDefinition} fontSize={16} color={theme.colors.neutral['500']} />
                    </Tooltip>}
                </View>
                : null}
            <MyDropDownPicker
                {...props}
                labelStyle={dropdownLabelStyle}
                borderColor={theme.colors.field.border}
                searchable={true}
                height={40}
                open={isOpen}
                setOpen={setIsOpen}
                dropDownDirection={props.dropDownDirection ?? 'BOTTOM'}
            />
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: (zIndex: number | undefined) => ({
        zIndex,
        minWidth: 200,
    }),
    filterText: (fontSize: number, semiBoldLabel: boolean) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
}));

export default FilterDropdown;
